/**
 * The OnboardingProfileInfo validation contract: the bounds the endpoint
 * enforces and the mapping from its rejections back to the UI fields.
 *
 * The endpoint answers 400 with ASP.NET model-state errors keyed by the
 * multipart field name (`HeightCM`, `DateOfBirth`, ...) while the screens
 * collect those answers under UI names. Mapping one to the other here lets both
 * platforms send the user back to the input that actually caused the rejection
 * instead of showing the generic "Invalid request" message.
 */
import { parseValidationErrors } from '../../utils/apiErrorHandler';
import type { ApiError } from '../../types';

export type ProfileInfoField =
  | 'gender'
  | 'country'
  | 'height'
  | 'weight'
  | 'dob'
  | 'profilePicture';

export type ProfileInfoFieldErrors = Partial<Record<ProfileInfoField, string>>;

/**
 * Bounds the backend enforces, mirrored so the screens can reject the value
 * before the request. Kept exactly as wide as the API to avoid refusing input
 * the server would have accepted.
 */
export const PROFILE_INFO_LIMITS = {
  HEIGHT_CM: { min: 0, max: 300 },
  MIN_AGE_YEARS: 13,
} as const;

const FIELD_BY_REQUEST_KEY: Record<string, ProfileInfoField> = {
  gender: 'gender',
  countryid: 'country',
  heightcm: 'height',
  heightunit: 'height',
  weightkg: 'weight',
  weightlbs: 'weight',
  weightunit: 'weight',
  dateofbirth: 'dob',
  profilepicture: 'profilePicture',
};

/** Fields owned by the basic-info step; a rejection here is correctable there. */
const BASIC_INFO_FIELDS: readonly ProfileInfoField[] = [
  'gender',
  'country',
  'height',
  'weight',
  'dob',
];

/**
 * Maps a rejected OnboardingProfileInfo request to the UI fields at fault.
 * Returns an empty object when the failure carries no model-state errors.
 */
export const getProfileInfoFieldErrors = (
  error: ApiError,
): ProfileInfoFieldErrors => {
  const fieldErrors: ProfileInfoFieldErrors = {};

  Object.entries(parseValidationErrors(error)).forEach(([key, message]) => {
    const field = FIELD_BY_REQUEST_KEY[key.trim().toLowerCase()];
    if (field && message && !fieldErrors[field]) {
      fieldErrors[field] = message;
    }
  });

  return fieldErrors;
};

export const hasBasicInfoFieldErrors = (
  fieldErrors: ProfileInfoFieldErrors,
): boolean => BASIC_INFO_FIELDS.some(field => Boolean(fieldErrors[field]));
